// The kiosk home screen. Designed to be read from across the kitchen and
// used with one tap: what's due now, big log buttons, what's coming up.

import { AppPromo } from './AppPromo.tsx';
import { AwakePlanCard, AwakePlanSheet, useAwakePlan } from './AwakePlan.tsx';
import { VisitorSheet, VisitorTips } from '../visitors/Visitors.tsx';
import { openVisit } from '../../domain/visitors.ts';
import {
  Bone,
  Cctv,
  DoorOpen,
  Brush,
  CalendarClock,
  ChevronRight,
  CircleCheck,
  CloudRain,
  Lightbulb,
  X,
  Droplet,
  Footprints,
  GraduationCap,
  HeartPulse,
  House,
  Moon,
  NotebookPen,
  Plus,
  Scale,
  StickyNote,
  Sun,
  ToyBrick,
  TriangleAlert,
  Utensils,
  History,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { SOCIAL_CATEGORIES } from '../../content/socialisation.ts';
import { SKILLS } from '../../content/training.ts';
import { deleteEvent, logEvent, openDuration, saveProfile, updateEvent } from '../../data/actions.ts';
import { useCollection, useDevice, useEvents, useHousehold, useLiveStatus, useProfile } from '../../data/hooks.ts';
import { formatMiles } from '../../domain/geo.ts';
import { toast } from '../../ui/toast.tsx';
import { finishWalk, gpsAvailable, startWalk } from '../walks/gps.ts';
import { careState, openCare } from '../../domain/care.ts';
import { LATE_DINNER, describeMeal, upcomingMeal } from '../../domain/feeding.ts';
import { seasonalAlerts } from '../../domain/milestones.ts';
import { accidentFreeSince, dayStats, toiletSuccess } from '../../domain/status.ts';
import { bedtimePending, napClash } from '../../domain/sleep.ts';
import { BED_JUST_OUT, BED_TOPUP, tileRelevance, type Relevance, type TileId } from '../../domain/relevance.ts';
import { HOUR, MINUTE, atClock, dayStart, formatDuration, formatTime, inClockWindow, relativeDay, startOfDay } from '../../domain/time.ts';
import { denWord, type LogEvent } from '../../domain/types.ts';
import { EVENT_META, describeEvent, joinNames } from '../../app/eventMeta.ts';
import { navigate } from '../../app/router.ts';
import { Avatar } from '../../ui/bits.tsx';
import { Badge, Card, CardHeader, Ring, Sheet, type Tone } from '../../ui/primitives.tsx';
import { openEditor, openNew } from '../log/EventEditor.tsx';
import { quickAccident, quickMeal, quickPoo, quickSimple, quickWee, toggleSleep, toggleTimed } from '../log/quick.ts';
import { Countdown } from './Countdown.tsx';
import { HeadsUpCard } from './HeadsUp.tsx';
import { TeamCard } from './Team.tsx';
import { WeekOneCard } from '../homecoming/WeekOne.tsx';
import { inWeekOne, planDay } from '../../domain/homecoming.ts';
import { validArrival } from '../../domain/firsts.ts';
import { useBrief, useTips } from '../../data/live.ts';
import { AskHero } from '../voice/AskHero.tsx';
import { PendingWalks } from '../walks/PendingWalks.tsx';
import { CatchUp } from '../log/CatchUp.tsx';
import { openEarlier } from '../log/Earlier.tsx';
import { toiletIntervalMinutes } from '../../domain/age.ts';
import { HealthWatchBanner } from '../health/HealthWatch.tsx';
import { TonightCard } from './Tonight.tsx';
import { AlbumCard } from '../diary/Album.tsx';
import { PooIcon } from '../../ui/icons.ts';
import { GoalsStrip } from '../training/Goals.tsx';
import { InsightOfTheDay } from '../insights/Daily.tsx';
import { HandoverCard } from './Handover.tsx';
import { useMoreBelow } from '../../ui/useMoreBelow.ts';
import { apiUrl } from '../../data/api.ts';
import { apptPlace, countdown, needsWriteUp, upcomingAppointments, whenLabel } from '../../domain/appointments.ts';
import { openAppointment, writeUp } from '../health/Appointments.tsx';
import { logTrainingSession, useTrainingWeek } from '../../data/training.ts';
import { SESSIONS_PER_DAY, planTitle, weekProgress } from '../../domain/trainingplan.ts';

export function Today() {
  const status = useLiveStatus(15_000);
  const profile = useProfile();
  const kiosk = !!useDevice().kiosk;
  if (!status.isHome) return <Countdown />;
  const weekOne = inWeekOne(planDay(profile.homeDate, status.now));
  const household = useHousehold();
  const evening = new Date(status.now).getHours() >= 19 || inClockWindow(status.now, household.nightStart, household.nightEnd);
  return (
    <div className="today">
      <div className="today-main">
        <AppPromo />
        <ArrivalCard />
        <HealthWatchBanner />
        <HandoverCard />
        <NowCard />
        <QuickLog />
      </div>
      <div className="today-side">
        {/* The kitchen tablet's side column is short: its rail Ask orb is enough. */}
        {!kiosk && <AskHero compact />}
        {/* The kitchen tablet has room for the whole plan beside its tiles. */}
        {kiosk && <AwakePlanCard />}
        {/* A habit of ours working against the goals: worth knowing before anything else. */}
        <HeadsUpCard />
        {/* From the evening through the night, tonight's plan matters most. */}
        {evening && <TonightCard />}
        {weekOne ? <WeekOneCard /> : <BriefCard />}
        {/* Things to do before things to know. */}
        <UpNext />
        <TeamCard />
        {/* The kitchen tablet's tiles need all the height, so its Help now sits here. */}
        {kiosk && (
          <Card className="help-card">
            <HelpStrip />
          </Card>
        )}
        <InsightOfTheDay compact={kiosk} />
        <GoalsStrip />
        {!evening && <TonightCard />}
        <AlbumCard />
        <TodayFeed />
      </div>
    </div>
  );
}

// --- Pick-up day -------------------------------------------------------------------

/** Pick-up day, until you tap it: the button for the moment he comes through the door. */
function ArrivalCard() {
  const profile = useProfile();
  const s = useLiveStatus(60_000);
  const guest = !!useDevice().guest;
  if (guest || validArrival(profile.homeDate, profile.arrivedAt) !== undefined || planDay(profile.homeDate, s.now) !== 0) return null;
  const name = profile.name || 'Arlo';
  return (
    <section className="arrival">
      <span className="arrival-photo">
        <img src={profile.photo ?? './arlo.jpg'} alt="" />
      </span>
      <div className="arrival-text">
        <span className="eyebrow">Today’s the day</span>
        <strong>{name} comes home today</strong>
        <span className="muted">Tap the button the moment he comes through the door — it starts his clock, and tells everyone.</span>
      </div>
      <button
        type="button"
        className="btn btn-primary btn-lg arrival-btn"
        onClick={() => {
          const at = Date.now();
          saveProfile({ arrivedAt: at });
          const note = logEvent('note', {}, { at, note: `${name} came home` });
          // A mis-tap shouldn't be forever.
          toast({
            message: `${name}’s home!`,
            detail: 'Both phones get a notification. Tapped too soon? Undo.',
            icon: House,
            tone: 'good',
            actions: [
              {
                label: 'Undo',
                onClick: () => {
                  saveProfile({ arrivedAt: undefined });
                  deleteEvent(note.id);
                },
              },
            ],
            duration: 60_000,
          });
        }}
      >
        <House className="btn-icon" aria-hidden />
        <span>He’s home!</span>
      </button>
    </section>
  );
}

// --- Now --------------------------------------------------------------------------

function NowCard() {
  const s = useLiveStatus(15_000);
  const household = useHousehold();
  const events = useEvents();
  const { toilet, sleep, meals, now, weeks } = s;
  const flat = household.home?.type === 'flat';
  const [catchUp, setCatchUp] = useState(false);
  const bedtime = useBedtime();
  // While he's up in the day: the awake-hour plan decides the headline, and the rest is a short "Then" list.
  const awake = useAwakePlan();
  const [planOpen, setPlanOpen] = useState(false);
  const toiletNow = !toilet.stale && (toilet.urgency === 'now' || toilet.urgency === 'overdue');
  // A wee due within 10 minutes beats starting a game: that's the headline ("Head out in 9 min").
  const toiletSoon = !toilet.stale && toilet.dueAt !== undefined && toilet.dueAt - now <= 10 * MINUTE;
  const planStep = awake && !toiletNow && !toiletSoon && (awake.plan.current === 'meal' || awake.plan.current === 'active' || awake.plan.current === 'calm') ? awake.plan.current : undefined;
  const nightNowEarly = inClockWindow(now, household.nightStart, household.nightEnd);
  const napNext =
    (awake?.plan.current === 'nap' && !toiletNow) ||
    (!sleep.asleep &&
    !bedtime &&
    !nightNowEarly &&
    !sleep.stale &&
    !toilet.stale &&
    sleep.napDueAt !== undefined &&
    sleep.napDueAt - now <= 10 * MINUTE &&
    toilet.dueAt !== undefined &&
    toilet.dueAt - now > 20 * MINUTE);
  // Pups this age usually poo several times a day, mostly after meals: a long gap since one, with food since, is worth a word.
  const lastMealAt = latestMeal(events, now);
  const pooNote =
    toilet.lastPoo && now - toilet.lastPoo.at > 4 * HOUR && lastMealAt !== undefined && lastMealAt > toilet.lastPoo.at
      ? `No poo since ${formatTime(toilet.lastPoo.at)} — give him a little longer outside next time.`
      : undefined;
  // Well past bedtime and still up: say so (gently) at the top of the card.
  const lateNight = bedtime && now - (atClock(now, household.nightStart) <= now ? atClock(now, household.nightStart) : atClock(dayStart(now, -1), household.nightStart)) > 15 * MINUTE;
  const aloneOpen = events.find((e) => e.type === 'alone' && e.end === undefined);
  const visit = openVisit(events, now);
  const [visitSheet, setVisitSheet] = useState(false);
  const [visitTips, setVisitTips] = useState(false);
  const stats = useMemo(() => dayStats(events, now), [events, now]);
  const success = toiletSuccess(stats);
  const rel = useTileRelevance();
  const cleanSince = accidentFreeSince(events);

  let tone: Tone = 'good';
  let title: ReactNode;
  let detail: ReactNode;
  let ring = 0;
  let ringLabel: ReactNode = null;

  if (sleep.asleep && sleep.current) {
    tone = 'sleep';
    const mins = now - sleep.current.at;
    title = 'Sleeping';
    detail =
      mins > 3 * 60 * MINUTE && !inClockWindow(now, household.nightStart, household.nightEnd)
        ? 'Still asleep after 3+ hours? If he’s actually up, tap “Woke up” so reminders restart.'
        : 'Let him sleep. When he wakes, straight outside to his spot.';
    ring = Math.min(1, mins / (120 * MINUTE));
    ringLabel = (
      <>
        <strong>{formatDuration(mins)}</strong>
        <span>asleep</span>
      </>
    );
  } else if (bedtime) {
    // Still up after bedtime (a late dinner or game): the next thing is bed, after a last trip out.
    tone = 'sleep';
    const bedAt = atClock(now, household.nightStart) <= now ? atClock(now, household.nightStart) : atClock(dayStart(now, -1), household.nightStart);
    const late = now - bedAt;
    const meal = latestMeal(events, now);
    // Has he already had his last wee? Out in the last 20 minutes counts; within the hour a top-up is optional.
    const lastOut = Math.max(toilet.lastWee?.at ?? 0, toilet.lastPoo?.at ?? 0);
    const sinceOut = now - lastOut;
    const wentAt = lastOut ? `${toilet.lastWee?.at === lastOut ? 'wee' : 'poo'} at ${formatTime(lastOut)}` : '';
    const outDone = lastOut > 0 && sinceOut <= BED_JUST_OUT;
    const topUp = lastOut > 0 && !outDone && sinceOut <= BED_TOPUP;
    title = outDone || topUp ? 'Bed time' : 'Last wee, then bed';
    const pooToo = meal !== undefined && now - meal < 3 * HOUR && (toilet.lastPoo?.at ?? 0) < meal;
    // Dinner well inside the ~2 hours before bed: a fuller tummy overnight.
    const lateDinner = meal !== undefined && meal > bedAt - LATE_DINNER;
    detail = (
      <>
        {outDone ? (
          <>He’s just been out ({wentAt}), so straight into his {denWord(household.den)} with a chew.</>
        ) : topUp ? (
          <>
            He went out at {formatTime(lastOut)} — that counts. A quick top-up wee just before his {denWord(household.den)} is optional but can buy a longer first stretch.
          </>
        ) : (
          <>A calm last trip out{pooToo ? ` — and a poo, he ate at ${formatTime(meal!)}` : ''}, then into his {denWord(household.den)} with a chew.</>
        )}{' '}
        Lights low, no more games.
        {lateDinner && <span className="now-hint"> Late dinner tonight, so a night trip may come a little sooner than usual.</span>}
      </>
    );
    ring = Math.min(1, late / HOUR);
    ringLabel =
      late > 10 * MINUTE ? (
        <>
          <strong>{formatDuration(late)}</strong>
          <span>past bed</span>
        </>
      ) : (
        <>
          <strong>Bed</strong>
          <span>time</span>
        </>
      );
  } else if (napNext) {
    // He's just been out and his nap's nearly due: the nap is what happens next, not a trip in 50 minutes.
    tone = 'sleep';
    const left = sleep.napDueAt! - now;
    title = left <= 0 ? 'Nap time' : `Nap in ${formatDuration(left, { long: true })}`;
    const lastOut = Math.max(toilet.lastWee?.at ?? 0, toilet.lastPoo?.at ?? 0);
    const justOut = lastOut > 0 && now - lastOut < 20 * MINUTE;
    detail = (
      <>
        {justOut
          ? <>He’s just been out ({toilet.lastPoo?.at === lastOut ? 'poo' : 'wee'} at {formatTime(lastOut)}), so into his {denWord(household.den)} with a chew, lights down.</>
          : <>A quick wee first, then into his {denWord(household.den)} with a chew, lights down.</>}{' '}
        Bitey or giddy is just tiredness.
        {pooNote && <span className="now-hint"> {pooNote}</span>}
      </>
    );
    ring = sleep.awakeFor !== undefined ? Math.min(1, sleep.awakeFor / (sleep.awakeFor + Math.max(0, left))) : 1;
    ringLabel = (
      <>
        <strong>{left > 0 ? formatDuration(left) : 'Now'}</strong>
        <span>{left > 0 ? 'to nap' : 'nap'}</span>
      </>
    );
  } else if (planStep && awake) {
    // Awake, been out, nap not yet due: what to do with him now.
    const napLeft = awake.plan.napAt - now;
    const ideas = (
      <button type="button" className="link" onClick={() => setPlanOpen(true)}>
        Ideas
      </button>
    );
    if (planStep === 'meal') {
      const m = awake.meal.meal;
      tone = 'good';
      const name = m ? describeMeal(m, meals.perDay).split(' · ')[0] : 'Meal';
      title = name === 'Afternoon' ? 'Afternoon meal' : `${name} time`;
      detail = <>{m ? (awake.meal.missed ? `Due at ${m.time} — not logged yet. ` : `Due ${m.time}. `) : ''}Keep back a handful of kibble for training.</>;
    } else if (planStep === 'active') {
      tone = 'accent';
      title = 'Play time';
      detail = <>10–15 minutes of one thing — a game, a little training or something new. {ideas}</>;
    } else {
      tone = 'info';
      title = 'Calm time';
      detail = <>Let him potter near you with a chew or lick mat. Can’t watch him? Pen with a Kong. {ideas}</>;
    }
    if (pooNote) detail = <>{detail} <span className="now-hint">{pooNote}</span></>;
    ring = awake.plan.progress;
    ringLabel = (
      <>
        <strong>{napLeft > 0 ? formatDuration(napLeft) : 'Now'}</strong>
        <span>to nap</span>
      </>
    );
  } else if (toilet.dueAt) {
    const left = toilet.dueAt - now;
    // Overnight a wake-up is a night trip: same urgency, but quiet and straight back to bed.
    const night = inClockWindow(now, household.nightStart, household.nightEnd);
    const last = Math.max(toilet.lastWee?.at ?? 0, toilet.lastPoo?.at ?? 0);
    const span = toilet.dueAt - (last || toilet.dueAt - 60 * MINUTE);
    ring = span > 0 ? Math.min(1, Math.max(0, 1 - left / span)) : 1;
    if (toilet.urgency === 'now' || toilet.urgency === 'overdue') {
      tone = toilet.urgency === 'overdue' ? 'bad' : 'warn';
      title = flat ? 'Head out now' : 'Toilet trip now';
      ringLabel = (
        <>
          <strong>Now</strong>
          <span>{left < -MINUTE ? `${formatDuration(-left)} over` : 'due'}</span>
        </>
      );
    } else {
      tone = toilet.urgency === 'soon' ? 'warn' : 'good';
      title = `${flat ? 'Head out' : 'Toilet trip'} in ${formatDuration(left, { long: true })}`;
      ringLabel = (
        <>
          <strong>{formatDuration(left)}</strong>
          <span>to go</span>
        </>
      );
    }
    if (night) title = toilet.urgency === 'now' || toilet.urgency === 'overdue' ? 'Night wee — out now' : `Night wee in ${formatDuration(left, { long: true })}`;
    detail = night ? (
      <>
        Night trip: {weeks < 16 ? 'carry him' : 'take him'} straight {flat ? 'down ' : ''}to {household.home?.toiletSpot || 'his spot'}, cue <strong>“{household.toiletCue ?? 'Be quick'}”</strong>, quiet praise — then straight back to bed. Lights low, no play, no chat.
      </>
    ) : (
      <>
        {/* Short: the ring already says how late, and the steps below say what next. */}
        {toilet.reason.split(' — ')[0]} · cue <strong>“{household.toiletCue ?? 'Be quick'}”</strong>, reward as he finishes
        {pooNote && <span className="now-hint">. {pooNote}</span>}
      </>
    );
  } else if (toilet.stale) {
    tone = 'neutral';
    title = 'Keep to the routine';
    detail = (
      <>
        {toilet.lastLogAt ? `Nothing logged since ${formatTime(toilet.lastLogAt)} — no problem.` : 'Nothing logged yet today.'} Out about every {toiletIntervalMinutes(weeks)} min while he’s awake, and straight after sleep, food and play.{' '}
        <button type="button" className="link" onClick={() => setCatchUp(true)}>
          Catch up
        </button>
      </>
    );
    ringLabel = (
      <>
        <strong>~{toiletIntervalMinutes(weeks)}m</strong>
        <span>rhythm</span>
      </>
    );
  } else {
    title = 'All good';
  }

  // Toilet trip and nap both within the next ~20 minutes: one plan, not two timers.
  const napSoon = sleep.napDueAt !== undefined && sleep.napDueAt - now < 20 * MINUTE;
  const toiletThenNap = !sleep.asleep && !sleep.stale && napSoon && toilet.dueAt !== undefined && toilet.dueAt - now < 25 * MINUTE;
  const nightNow = inClockWindow(now, household.nightStart, household.nightEnd);
  // While he's up in the day the "Then" list covers the nap.
  const napLine = bedtime || napNext || awake
    ? undefined
    : !sleep.asleep && nightNow
      ? `Night-time — back to bed straight after his wee (night ends ${household.nightEnd})`
      : !sleep.asleep && sleep.stale && sleep.wokeAt !== undefined
      ? `No nap logged since ${formatTime(sleep.wokeAt)}`
      : !sleep.asleep && sleep.awakeFor !== undefined
      ? toiletThenNap
        ? `Awake ${formatDuration(sleep.awakeFor)} · nap ${sleep.napDueAt! > now ? `in ${formatDuration(sleep.napDueAt! - now)}` : 'due'} after his wee`
        : sleep.napUrgency === 'ok' || sleep.napUrgency === 'soon'
        ? `Awake ${formatDuration(sleep.awakeFor)} · nap due ${sleep.napDueAt && sleep.napDueAt > now ? `in ${formatDuration(sleep.napDueAt - now)}` : 'now'}`
        : `Awake ${formatDuration(sleep.awakeFor)} — nap time (overtired puppies get bitey)`
      : undefined;

  // Then: the next few timed things, soonest first — whatever isn't already the headline.
  const coming: { at: number; label: string }[] = [];
  if (awake) {
    if (!toiletNow && !toiletSoon && toilet.dueAt !== undefined && !napNext) coming.push({ at: toilet.dueAt, label: 'Wee' });
    const m = awake.meal.meal;
    // Only this awake spell: a meal after his nap isn't "then".
    if (m && planStep !== 'meal' && m.at <= awake.plan.napAt + 5 * MINUTE) coming.push({ at: m.at, label: `${describeMeal(m, meals.perDay).split(' · ')[0]}${awake.meal.missed ? ' · not logged?' : ''}` });
    if (!napNext) {
      // A wee and the nap within a quarter of an hour of each other are one job.
      const wee = coming.find((c) => c.label === 'Wee');
      if (wee && Math.abs(wee.at - awake.plan.napAt) <= 15 * MINUTE) {
        wee.at = Math.min(wee.at, awake.plan.napAt);
        wee.label = "Wee, then nap";
      } else coming.push({ at: awake.plan.napAt, label: `Nap in his ${awake.den}` });
    }
    coming.sort((a, b) => a.at - b.at);
  }

  return (
    <Card className={`now-card tone-${tone}`}>
      <div className="now-main">
        <Ring value={ring} size={128} stroke={11} tone={tone}>
          {ringLabel}
        </Ring>
        <div className="now-text">
          <span className="eyebrow">{bedtime ? (lateNight ? 'Late night' : 'Bedtime') : sleep.asleep || napNext || planStep ? 'Right now' : 'Toilet training'}</span>
          <h2 className="now-title">{title}</h2>
          <p className="now-detail">{detail}</p>
          {aloneOpen && (
            <p className="now-nap">
              <House aria-hidden /> Home alone for {formatDuration(now - aloneOpen.at)}
              {aloneOpen.source === 'automation' ? ' (you’re both out)' : ''} ·{' '}
              <a className="link" href="#/camera">
                camera
              </a>
            </p>
          )}
          {visit && (
            <p className="now-nap now-visit">
              <DoorOpen aria-hidden />
              <span className="now-nap-text">
                {joinNames(visit.data.names)} {now - visit.at < 2 * MINUTE ? 'just arrived' : `${visit.data.names.length > 1 ? 'are' : 'is'} here · ${formatDuration(now - visit.at)}`}
              </span>
              <button type="button" className="link now-help" onClick={() => setVisitTips(true)}>
                Visitor tips
              </button>
              <button type="button" className="link now-help" onClick={() => setVisitSheet(true)}>
                They’ve left
              </button>
              <button type="button" className="link now-help" onClick={() => openEditor(visit)}>
                Edit
              </button>
            </p>
          )}
          {napLine && (
            // On phones the checklist below already has the nap as a step.
            <p className={`now-nap${!nightNow && (sleep.napUrgency === 'now' || sleep.napUrgency === 'overdue') ? ' warn' : ''}${rel.nap ? ' in-steps' : ''}`}>
              <Moon aria-hidden />
              <span className="now-nap-text">{napLine}</span>
              {!sleep.asleep && sleep.stale && !nightNow && (
                <button type="button" className="link now-help" onClick={() => openEarlier('nap')}>
                  Missed one? Add it
                </button>
              )}
              {!nightNow && (sleep.napUrgency === 'now' || sleep.napUrgency === 'overdue') && (
                <button type="button" className="link now-help" onClick={() => navigate('/training/help?b=bitey')}>
                  Bitey? What to do
                </button>
              )}
            </p>
          )}
          {coming.length > 0 && (
            <div className="now-then">
              <span className="eyebrow">Then</span>
              <ul>
                {coming.slice(0, 3).map((c) => (
                  <li key={c.label}>
                    <span className="now-then-time">{c.at <= now ? 'now' : formatTime(c.at)}</span>
                    <span>{c.label}</span>
                  </li>
                ))}
              </ul>
              {/* Play and calm time already have "Ideas" — one way into the plan is enough. */}
              {planStep !== 'active' && planStep !== 'calm' && (
                <button type="button" className="link small" onClick={() => setPlanOpen(true)}>
                  Awake plan
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {/* The "Then" list replaces the phone checklist while he's up in the day. */}
      {!awake && <NextUp />}
      {awake && <AwakePlanSheet ctx={awake} open={planOpen} onClose={() => setPlanOpen(false)} />}
      <VisitorSheet open={visitSheet} onClose={() => setVisitSheet(false)} />
      <VisitorTips open={visitTips} onClose={() => setVisitTips(false)} />
      <div className="now-stats">
        <Stat label="Since wee" value={toilet.lastWee ? formatDuration(now - toilet.lastWee.at) : '—'} />
        <Stat label="Since poo" value={toilet.lastPoo ? formatDuration(now - toilet.lastPoo.at) : '—'} />
        <Stat label="Meals" value={`${meals.eatenToday} / ${meals.perDay}`} />
        <Stat label="Outside today" value={success !== undefined ? `${Math.round(success * 100)}%` : '—'} />
        <Stat
          label="Accident-free"
          value={cleanSince ? formatDuration(now - cleanSince) : '—'}
          hint={weeks < 16 ? 'Accidents are normal for months' : undefined}
        />
      </div>
      <CatchUp open={catchUp} onClose={() => setCatchUp(false)} since={toilet.lastLogAt} />
    </Card>
  );
}

function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="stat" title={hint}>
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

// --- Morning brief -------------------------------------------------------------------

function BriefCard() {
  const b = useBrief();
  return (
    <Card className="brief-card">
      <div className="brief-head">
        <span className="eyebrow">{b.via === 'ai' ? 'Today’s brief · written for you' : 'Today’s brief'}</span>
        <button type="button" className="link small" onClick={() => navigate('/diary')}>
          His week <ChevronRight aria-hidden width={14} />
        </button>
      </div>
      <strong className="brief-title">{b.title}</strong>
      <p className="brief-body">{b.body}</p>
    </Card>
  );
}

// --- Quick log -----------------------------------------------------------------------

/** Today's shortcuts to Training → Help now. */
const HELP_SHORTCUTS: [string, string][] = [
  ['bitey', 'Bitey'],
  ['zoomies', 'Zoomies'],
  ['wont-settle', 'Won’t settle'],
  ['crate-crying', 'Crying'],
  ['velcro', 'Clingy'],
  ['pen', 'Pen protest'],
];

/** Which Quick log tiles matter right now (domain/relevance.ts), from the live status and the log. */
const latestMeal = (events: LogEvent[], now: number) => events.reduce<number | undefined>((m, e) => (e.type === 'meal' && e.at <= now && (m === undefined || e.at > m) ? e.at : m), undefined);

/** Past bedtime and still up: the routine is a last wee, then bed (not a "night wee"). */
function useBedtime(): boolean {
  const s = useLiveStatus(15_000);
  const events = useEvents();
  const household = useHousehold();
  return !s.sleep.asleep && bedtimePending(events, s.now, { start: household.nightStart, end: household.nightEnd });
}

function useTileRelevance() {
  const s = useLiveStatus(15_000);
  const events = useEvents();
  const household = useHousehold();
  const next = upcomingMeal(s.meals, s.now).meal;
  return useMemo(() => {
    const dayStart = startOfDay(s.now);
    const lastMeal = events.filter((e) => e.type === 'meal' && e.at <= s.now).reduce((m, e) => Math.max(m, e.at), 0);
    return tileRelevance({
      now: s.now,
      night: inClockWindow(s.now, household.nightStart, household.nightEnd),
      bedtime: !s.sleep.asleep && bedtimePending(events, s.now, { start: household.nightStart, end: household.nightEnd }),
      asleep: s.sleep.asleep,
      toiletDueAt: s.toilet.dueAt,
      toiletUrgency: s.toilet.urgency,
      toiletReason: s.toilet.reason,
      toiletStale: s.toilet.stale,
      napDueAt: s.sleep.napDueAt,
      napUrgency: s.sleep.napUrgency,
      sleepStale: s.sleep.stale,
      wokeAt: s.sleep.wokeAt,
      lastWeeAt: s.toilet.lastWee?.at,
      lastPooAt: s.toilet.lastPoo?.at,
      lastMealAt: lastMeal || undefined,
      nextMealAt: next?.at,
      trainingToday: events.filter((e) => e.type === 'training' && e.at >= dayStart && e.at <= s.now).length,
      trainingTarget: SESSIONS_PER_DAY,
    });
  }, [s, events, next?.at, household.nightStart, household.nightEnd]);
}

/**
 * Phones: the Quick log tiles are below the fold, so what to do now sits in
 * the Now card: the first thing as one clear button, the rest as "then…"
 * chips — in the order you'd do them (out for a wee before food, the nap last).
 */
const NEXT_ORDER: TileId[] = ['wee', 'poo', 'meal', 'training', 'play', 'nap'];

/** Phones: what to do now, as a short numbered checklist — tap a step when it's done. */
function NextUp() {
  const rel = useTileRelevance();
  const bedtime = useBedtime();
  const s = useLiveStatus(15_000);
  const grams = upcomingMeal(s.meals, s.now).meal?.grams;
  const den = denWord(useHousehold().den);
  const items = (
    [
      ['wee', Droplet, 'Wee outside', 'warn', quickWee],
      ['poo', PooIcon, 'Poo outside', 'accent', quickPoo],
      ['nap', Moon, `Nap in his ${den}`, 'sleep', toggleSleep],
      ['meal', Utensils, 'Meal', 'good', () => quickMeal(grams)],
      ['training', GraduationCap, 'Training', 'info', () => openNew('training', { skills: [] })],
      ['play', ToyBrick, 'Play', 'accent', () => toggleTimed('play')],
    ] as [TileId, LucideIcon, string, Tone, () => void][]
  )
    .filter(([id]) => rel[id])
    .sort((a, b) => NEXT_ORDER.indexOf(a[0]) - NEXT_ORDER.indexOf(b[0]));
  if (!items.length) return null;
  if (bedtime)
    for (const it of items) {
      if (it[0] === 'nap') it[2] = `Bed in his ${den}`;
      // He's been out within the hour: the wee is a top-up, not a missed one.
      if (it[0] === 'wee' && rel.wee?.level === 'soon') it[2] = 'Top-up wee (optional)';
    }
  const toiletFirst = items[0][0] === 'wee' || items[0][0] === 'poo';
  return (
    <ol className="next-up" aria-label="What to do now">
      {items.map(([id, Icon, label, tone, fn], i) => {
        // The nap comes after the toilet trip — say so rather than "overdue".
        const sub = id === 'nap' && toiletFirst && !bedtime ? 'after his wee' : rel[id]!.sub;
        return (
          <li key={id}>
            <button type="button" className={`next-step tone-${tone}${i === 0 ? ' first' : ''}`} onClick={fn}>
              <span className="next-num">{i + 1}</span>
              <Icon aria-hidden className="next-icon" />
              <strong>{label}</strong>
              <small>{sub}</small>
              <CircleCheck aria-label="Done" className="next-done" />
            </button>
          </li>
        );
      })}
    </ol>
  );
}

/** "Help now": what to do when he's bitey, wild, crying… (Training → Help now). */
function HelpStrip() {
  return (
    <div className="help-strip" aria-label="Help right now">
      <span className="eyebrow">Help now</span>
      {HELP_SHORTCUTS.map(([id, label]) => (
        <button key={id} type="button" className="chip" onClick={() => navigate(`/training/help?b=${id}`)}>
          {label}
        </button>
      ))}
      <button type="button" className="chip" onClick={() => navigate('/training/games')}>
        Games
      </button>
    </div>
  );
}

function QuickLog() {
  const s = useLiveStatus(15_000);
  const device = useDevice();
  const events = useEvents(); // re-render when durations open/close
  const [more, setMore] = useState(false);
  const [visitor, setVisitor] = useState(false);
  const visit = openVisit(events, s.now);
  const [accident, setAccident] = useState(false);
  const walking = openDuration('walk');
  const playing = openDuration('play');
  const meal = upcomingMeal(s.meals, s.now);
  const next = meal.meal;
  // What matters now: due tiles glow and pulse, coming-up ones get an outline — and say why.
  const rel = useTileRelevance();
  const bedtime = useBedtime();
  // Several due at once: only the first thing to do pulses; the rest are marked, quietly.
  const lead = NEXT_ORDER.find((id) => rel[id]?.level === 'now');
  const calm = (id: TileId) => !!lead && lead !== id;

  return (
    <Card className="quick-card">
      <div className="quick-grid">
        <QuickButton icon={Droplet} label="Wee" sub="outside" rel={rel.wee} calm={calm('wee')} tone="warn" onClick={quickWee} />
        <QuickButton icon={PooIcon} label="Poo" sub="outside" rel={rel.poo} calm={calm('poo')} tone="accent" onClick={quickPoo} />
        {/* Always there, never shouting: a red tile all day reads as "something's wrong". */}
        <QuickButton icon={TriangleAlert} label="Accident" sub="indoors" tone="bad" quiet onClick={() => setAccident(true)} />
        <QuickButton
          icon={Utensils}
          label="Meal"
          sub={meal.missed ? meal.label : next ? `${next.grams ? `${next.grams} g · ` : ''}${next.time}` : meal.label}
          calm={calm('meal')}
          rel={rel.meal && { ...rel.meal, sub: `${next?.grams ? `${next.grams} g · ` : ''}${rel.meal.sub}` }}
          tone="good"
          onClick={() => quickMeal(next?.grams)}
        />
        <QuickButton
          icon={s.sleep.asleep ? Sun : Moon}
          label={s.sleep.asleep ? 'Woke up' : bedtime ? 'Bed' : 'Nap'}
          sub={s.sleep.asleep && s.sleep.current ? `asleep ${formatDuration(s.now - s.sleep.current.at)}` : bedtime ? 'lights out' : 'start sleep'}
          rel={rel.nap} calm={calm('nap')}
          tone="sleep"
          active={s.sleep.asleep}
          onClick={toggleSleep}
        />
        <QuickButton
          icon={Footprints}
          label={walking ? 'Back home' : 'Outing'}
          sub={walking ? formatDuration(s.now - walking.at) : s.weeks < 12 ? 'carry him out' : 'walk'}
          tone="good"
          active={!!walking}
          onClick={() => {
            if (walking) {
              const w = finishWalk();
              if (w?.end) toast({ message: `Back home · ${formatDuration(w.end - w.at)}`, detail: w.type === 'walk' && w.data.distanceKm ? formatMiles(w.data.distanceKm) : undefined, tone: 'good', icon: Footprints });
            } else {
              startWalk(!device.kiosk && gpsAvailable);
              toast({ message: 'Outing started', detail: !device.kiosk && gpsAvailable ? 'Recording distance — open Walks for the live view.' : undefined, tone: 'good', icon: Footprints, actions: [{ label: 'Live view', onClick: () => navigate('/walks') }] });
            }
          }}
        />
        <QuickButton
          icon={ToyBrick}
          label={playing ? 'Stop play' : 'Play'}
          sub={playing ? formatDuration(s.now - playing.at) : 'start'}
          rel={rel.play} calm={calm('play')}
          tone="accent"
          active={!!playing}
          onClick={() => toggleTimed('play')}
        />
        <QuickButton icon={GraduationCap} label="Training" sub="log session" rel={rel.training} calm={calm('training')} tone="info" onClick={() => openNew('training', { skills: [] })} />
        {/* Visitors (and, from the same sheet, any other new experience). */}
        <QuickButton
          icon={DoorOpen}
          label={visit ? 'Visitors' : 'Visitor'}
          sub={visit ? `${visit.data.names.map((n) => n.split(' ')[0]).join(', ')}${s.now - visit.at < 2 * MINUTE ? '' : ` · ${formatDuration(s.now - visit.at)}`}` : 'or new thing'}
          tone="good"
          active={!!visit}
          onClick={() => setVisitor(true)}
        />
        <QuickButton icon={Plus} label="More" sub="weight, meds…" tone="neutral" onClick={() => setMore(true)} />
      </div>
      {/* The tablet's tiles need all the height (it has Earlier nap in More); phones get the shortcuts. */}
      {!device.kiosk && (
        <button type="button" className="link quick-earlier" onClick={() => openEarlier('nap')}>
          <History aria-hidden className="inline-icon" /> Forgot to log something? Add it from earlier
        </button>
      )}
      {!device.kiosk && <HelpStrip />}
      <VisitorSheet open={visitor} onClose={() => setVisitor(false)} />
      <Sheet open={accident} onClose={() => setAccident(false)} title="Accident indoors">
        <p className="muted">No telling off — he doesn’t understand yet. Interrupt calmly if you catch him mid-way and pop him outside.</p>
        <div className="quick-grid quick-grid-2">
          <QuickButton icon={Droplet} label="Wee" tone="warn" onClick={() => (quickAccident('wee'), setAccident(false))} />
          <QuickButton icon={PooIcon} label="Poo" tone="accent" onClick={() => (quickAccident('poo'), setAccident(false))} />
        </div>
      </Sheet>
      <Sheet open={more} onClose={() => setMore(false)} title="Log something else">
        <div className="quick-grid quick-grid-3">
          {(
            [
              [Scale, 'Weight', () => openNew('weight', { kg: 0 })],
              [Brush, 'Grooming', () => openNew('groom', { tasks: ['brush'] })],
              [House, 'Alone time', () => toggleTimed('alone')],
              [Bone, 'Chew / Kong', () => quickSimple('treat')],
              [HeartPulse, 'Health', () => openNew('health', { symptom: 'other', severity: 1 })],
              [CalendarClock, 'Medication', () => openNew('meds', { name: '' })],
              [StickyNote, 'Note', () => openNew('note', {})],
              [Moon, 'Earlier nap', () => openEarlier('nap')],
              [History, 'Something earlier', () => openEarlier('wee')],
            ] as [LucideIcon, string, () => void][]
          ).map(([icon, label, fn]) => (
            <QuickButton key={label} icon={icon} label={label} tone="neutral" onClick={() => (setMore(false), fn())} />
          ))}
        </div>
      </Sheet>
    </Card>
  );
}

function QuickButton({ icon: Icon, label, sub, tone, onClick, active, rel, quiet, calm }: { icon: LucideIcon; label: string; sub?: string; tone: Tone; onClick: () => void; active?: boolean; rel?: Relevance; quiet?: boolean; calm?: boolean }) {
  const level = active ? undefined : rel?.level;
  return (
    <button type="button" className={`quick tone-${tone}${quiet ? ' quiet' : ''}${active ? ' active' : level ? ` ${level === 'now' ? 'due' : 'soon'}` : ''}${calm ? ' calm' : ''}`} onClick={onClick}>
      {level && (
        <span className="quick-badge" aria-hidden>
          {level === 'now' ? 'Now' : 'Soon'}
        </span>
      )}
      <span className="quick-icon">
        <Icon aria-hidden />
      </span>
      <span className="quick-label">{label}</span>
      {(level ? rel!.sub : sub) && <span className="quick-sub">{level ? rel!.sub : sub}</span>}
    </button>
  );
}

// --- Up next ------------------------------------------------------------------------------

interface NextItem {
  key: string;
  icon: LucideIcon;
  title: string;
  detail?: string;
  tone: Tone;
  badge?: string;
  onClick?: () => void;
  onDismiss?: () => void;
}

function UpNext() {
  const s = useLiveStatus(60_000);
  const care = useCollection('care');
  const skills = useCollection('skills');
  const events = useEvents();
  const profile = useProfile();

  const { tips, dismiss } = useTips(2);
  const appts = useCollection('appointments');
  const contacts = useCollection('contacts');
  const plan = useTrainingWeek();
  const guest = !!useDevice().guest;
  const items = useMemo(() => {
    const out: NextItem[] = [];
    // Booked appointments in the next day and a half, then any vet visit waiting to be written up.
    for (const a of upcomingAppointments(appts, s.now).filter((a) => a.at - s.now < 36 * 60 * MINUTE)) {
      const soon = countdown(a.at, s.now);
      const place = apptPlace(a, contacts);
      out.push({
        key: `appt-${a.id}`,
        icon: CalendarClock,
        title: `${a.title} · ${whenLabel(a.at, s.now)}`,
        detail: [place, a.notes].filter(Boolean).join(' — ') || undefined,
        tone: a.at - s.now < 2 * 60 * MINUTE ? 'warn' : 'info',
        badge: a.at <= s.now ? 'now' : soon,
        onClick: () => openAppointment({}, a),
      });
    }
    for (const a of needsWriteUp(appts, s.now).slice(0, 1))
      out.push({ key: `writeup-${a.id}`, icon: NotebookPen, title: `How did it go? ${a.title}`, detail: 'Write down what the vet said, what was given and the cost.', tone: 'warn', onClick: () => writeUp(a, apptPlace(a, contacts)) });
    if (s.meals.next) {
      const m = s.meals.next;
      const late = m.at < s.now - 30 * MINUTE;
      const probablyForgot = m.at < s.now - 90 * MINUTE;
      out.push(
        probablyForgot
          ? {
              key: 'meal',
              icon: Utensils,
              title: `${describeMeal(m, s.meals.perDay).split(' · ')[0]} not logged`,
              detail: `Did he have it around ${m.time}? Tap to log it as eaten.`,
              tone: 'neutral',
              onClick: () => {
                logEvent('meal', { grams: m.grams, appetite: 'all' }, { at: m.at });
                toast({ message: 'Meal logged', tone: 'good', icon: Utensils });
              },
            }
          : {
              key: 'meal',
              icon: Utensils,
              title: describeMeal(m, s.meals.perDay),
              detail: m.grams ? `${m.grams} g · meal ${m.index + 1} of ${s.meals.perDay}` : 'Set daily grams on the Food page',
              tone: late ? 'warn' : 'good',
              badge: late ? 'due' : undefined,
              onClick: () => navigate('/food'),
            },
      );
    }
    // A care job that's booked as an appointment shows once, as the appointment.
    const booked = new Set(upcomingAppointments(appts, s.now).flatMap((a) => a.careIds ?? []));
    const dueCare = openCare(care).filter((c) => !booked.has(c.id) && !['upcoming', 'unscheduled'].includes(careState(c, s.now)));
    const overdue = dueCare.filter((c) => careState(c, s.now) === 'overdue');
    if (overdue.length > 2)
      out.push({
        key: 'overdue',
        icon: HeartPulse,
        title: `${overdue.length} care jobs to tick off`,
        detail: overdue
          .slice(0, 3)
          .map((c) => c.title)
          .join(' · '),
        tone: 'warn',
        badge: 'catch up',
        onClick: () => navigate('/health'),
      });
    for (const c of dueCare.filter((c) => overdue.length <= 2 || !overdue.includes(c)).slice(0, 4)) {
      const st = careState(c, s.now);
      out.push({
        key: c.id,
        icon: HeartPulse,
        title: c.title,
        detail: c.due ? relativeDay(c.due, s.now) : undefined,
        tone: st === 'overdue' ? 'bad' : st === 'due' ? 'warn' : 'info',
        badge: st === 'overdue' ? 'overdue' : st === 'due' ? 'today' : undefined,
        onClick: () => navigate('/health'),
      });
    }
    // This week's training plan: tap after a session to log it.
    if (plan) {
      const p = weekProgress(plan, events, s.now);
      const done = p.today >= SESSIONS_PER_DAY;
      out.push({
        key: 'train',
        icon: GraduationCap,
        title: `Training: ${planTitle(plan)}`,
        detail: done ? `All ${SESSIONS_PER_DAY} sessions done today — nice. ${p.total} this week.` : `${p.today} of ${SESSIONS_PER_DAY} five-minute sessions today — tap when you’ve done one.`,
        tone: done ? 'good' : 'info',
        badge: `${Math.min(p.today, SESSIONS_PER_DAY)}/${SESSIONS_PER_DAY}`,
        onClick: guest
          ? () => navigate('/training')
          : () => {
              const e = logTrainingSession(plan);
              toast({ message: 'Training session logged', detail: planTitle(plan), tone: 'good', icon: GraduationCap, actions: [{ label: 'Undo', onClick: () => deleteEvent(e.id) }] });
            },
      });
    } else {
      // Before the week's plan exists (e.g. before he's home): the first skill for his age.
      const levels = new Map(skills.map((k) => [k.id, k.level]));
      const focus = SKILLS.filter((k) => k.fromWeeks <= s.weeks + 1 && (levels.get(k.id) ?? 0) < 3).slice(0, 1)[0];
      if (focus) out.push({ key: 'train', icon: GraduationCap, title: `Practise: ${focus.name}`, detail: focus.summary, tone: 'info', onClick: () => navigate(`/training/${focus.id}`) });
    }
    // Socialisation idea of the day: something not yet experienced, stable for the day.
    if (s.weeks < 20) {
      const seen = new Set(events.filter((e): e is LogEvent<'social'> => e.type === 'social').map((e) => e.data.itemId));
      const pool = SOCIAL_CATEGORIES.flatMap((c) => c.items).filter((i) => !seen.has(i.id) && (s.weeks >= 12 || !i.afterVaccination));
      if (pool.length) {
        const pick = pool[Math.floor(startOfDay(s.now) / 86_400_000) % pool.length];
        out.push({ key: 'social', icon: EVENT_META.social.icon, title: `Try today: ${pick.label}`, detail: pick.tip, tone: 'good', onClick: () => navigate('/social') });
      }
    }
    // Tips after the things to do — minus the ones the insights already cover (accident hours, sleep).
    out.push(
      ...tips
        .filter((t) => !/^(accident-hour|sleep)-/.test(t.id))
        .map((t) => ({
          key: `tip-${t.id}`,
          icon: t.kind === 'weather' ? CloudRain : Lightbulb,
          title: t.title,
          detail: t.body,
          tone: 'accent' as Tone,
          onClick: t.route ? () => navigate(t.route!) : undefined,
          onDismiss: () => dismiss(t.id),
        })),
    );
        const season = seasonalAlerts(s.now)[0];
    if (season) out.push({ key: season.id, icon: TriangleAlert, title: season.title, detail: season.detail, tone: season.level === 'warn' ? 'warn' : 'info', onClick: () => navigate('/guide') });
    return out;
  }, [s, care, skills, events, tips, appts, contacts, plan, guest]);
  const nextMore = useMoreBelow<HTMLUListElement>([items.length]);

  return (
    <Card className="upnext-card">
      <CardHeader title="Up next" icon={CalendarClock} sub={profile.dob ? undefined : 'Add his date of birth in Settings for a full plan'} />
      <PendingWalks />
      <ul className={`next-list${nextMore.more ? ' has-more' : ''}`} ref={nextMore.ref}>
        {items.map((i) => (
          <li key={i.key}>
            <button type="button" className={`next-item tone-${i.tone}`} onClick={i.onClick}>
              <span className="next-icon">
                <i.icon aria-hidden />
              </span>
              <span className="next-text">
                <strong>{i.title}</strong>
                {i.detail && <span className="muted">{i.detail}</span>}
              </span>
              {i.badge && <Badge tone={i.tone}>{i.badge}</Badge>}
              {!i.onDismiss && <ChevronRight className="chev" aria-hidden />}
            </button>
            {i.onDismiss && (
              <button type="button" className="next-dismiss" aria-label="Dismiss tip" onClick={i.onDismiss}>
                <X aria-hidden />
              </button>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

// --- Today's feed -------------------------------------------------------------------------

function TodayFeed() {
  const events = useEvents();
  const household = useHousehold();
  const s = useLiveStatus(60_000);
  const since = startOfDay(s.now) - (new Date(s.now).getHours() < 6 ? 6 * 60 * MINUTE : 0);
  const today = events.filter((e) => e.at >= since && e.type !== 'tracker' && !(e.type === 'social' && e.data.visitId));
  const feedMore = useMoreBelow<HTMLOListElement>([today.length]);
  return (
    <Card className="feed-card">
      <CardHeader title="Today" icon={CalendarClock} action={<a href="#/timeline" className="link">All</a>} />
      {today.length === 0 ? (
        <p className="muted feed-empty">Nothing logged yet today. Tap a button when something happens — it all adds up to a picture of his routine.</p>
      ) : (
        <ol className={`feed${feedMore.more ? ' has-more' : ''}`} ref={feedMore.ref}>
          {today.map((e) => (
            <FeedRow key={e.id} e={e} person={household.people.find((p) => p.id === e.by)} />
          ))}
        </ol>
      )}
    </Card>
  );
}

export function FeedRow({ e, person }: { e: LogEvent; person?: import('../../domain/types.ts').Person }) {
  const meta = EVENT_META[e.type];
  const d = describeEvent(e, {
    skill: (id) => SKILLS.find((k) => k.id === id)?.name,
    social: (id) => SOCIAL_CATEGORIES.flatMap((c) => c.items).find((i) => i.id === id)?.label,
  });
  const accident = (e.type === 'wee' || e.type === 'poo') && e.data.where === 'inside';
  return (
    <li>
      <button type="button" className={`feed-row tone-${accident ? 'bad' : meta.tone}`} onClick={() => openEditor(e)}>
        <span className="feed-time">{formatTime(e.at)}</span>
        <span className="feed-icon">
          <meta.icon aria-hidden />
        </span>
        <span className="feed-text">
          <strong>{d.title}</strong>
          {(d.detail || e.note) && <span className="muted">{[d.detail, e.note].filter(Boolean).join(' · ')}</span>}
        </span>
        {person && <Avatar person={person} size={26} />}
      </button>
      {e.type === 'camera' && <CameraExtras e={e} />}
      {e.type === 'sleep' && <NapCheck nap={e} />}
    </li>
  );
}

/** A nap with a wee, meal or training logged in the middle of it: he was up by then — one tap ends the nap there. */
function NapCheck({ nap }: { nap: LogEvent<'sleep'> }) {
  const events = useEvents();
  const household = useHousehold();
  const hit = napClash(nap, events, Date.now(), { start: household.nightStart, end: household.nightEnd });
  if (!hit) return null;
  const fix = () => {
    updateEvent({ ...nap, end: hit.at });
    toast({
      message: `Nap now ${formatTime(nap.at)}–${formatTime(hit.at)} · ${formatDuration(hit.at - nap.at)}`,
      icon: EVENT_META.sleep.icon,
      tone: 'good',
      actions: [{ label: 'Undo', onClick: () => updateEvent(nap) }],
    });
  };
  return (
    <div className="feed-check">
      <TriangleAlert aria-hidden className="inline-icon" />
      <span>
        {EVENT_META[hit.type].label} at {formatTime(hit.at)} is logged during this nap, so he was up by then.
      </span>
      <button type="button" className="btn btn-soft btn-sm" onClick={fix}>
        Woke at {formatTime(hit.at)}
      </button>
    </div>
  );
}

/** A detection's frame and a link to its clip, when the camera system gave us them. */
function CameraExtras({ e }: { e: LogEvent<'camera'> }) {
  const household = useHousehold();
  const [broken, setBroken] = useState(false);
  const clip = clipLink(e, household.protectLink);
  const src = e.data.snapshot && e.data.snapshotToken && !broken ? apiUrl(`/api/photos/${e.data.snapshot}?t=${encodeURIComponent(e.data.snapshotToken)}`) : undefined;
  if (!clip && !src) return null;
  return (
    <div className="feed-cam">
      {src && (
        <a href={src} target="_blank" rel="noreferrer" className="feed-cam-shot">
          <img src={src} alt={`What the camera saw at ${formatTime(e.at)}`} loading="lazy" onError={() => setBroken(true)} />
        </a>
      )}
      {clip && (
        <a href={clip} target="_blank" rel="noreferrer" className="btn btn-soft btn-sm">
          <Cctv className="btn-icon" aria-hidden />
          <span>Watch clip</span>
        </a>
      )}
    </div>
  );
}

/** The clip in UniFi Protect: via the console's web address if set (works anywhere), else the link Protect sent. */
export function clipLink(e: LogEvent<'camera'>, base?: string): string | undefined {
  const web = (s?: string) => (s && /^https?:\/\//.test(s) ? s : undefined);
  if (web(base) && e.data.eventId) return `${base!.replace(/\/+$/, '')}/protect/events/event/${encodeURIComponent(e.data.eventId)}`;
  return e.source === 'automation' ? web(e.data.clip) : undefined;
}
